import { useEffect, useMemo } from 'react';
import { useApi } from '@paradigm/api/src';
import { useObservable } from '@paradigm/utils/src/hooks';
import {
  TradeTapeItem,
  Trade as DrfqTrade,
  isExecutedTrade,
} from '#/unified-rfqs/entities/drfq/trades';
import {
  useDrfqOrderbook,
  useDrfqOrders,
  useDrfqService,
} from '#/unified-rfqs/contexts/drfqs';
import { useUnifiedTradesService } from '#/unified-rfqs/contexts/unified-trades';
import { Drfq, isAsyncVenue } from '#/unified-rfqs/entities/drfq/domain';
import {
  isBidOrder,
  isOrderExecuted,
  Order,
} from '#/unified-rfqs/entities/drfq/orders';
import { Side } from '#/unified-rfqs/entities/shared';
import useFetchedDrfq from '#/unified-rfqs/contexts/use-fetched-drfq';

export function useTakerOrderbookFetcher(
  rfqId: Drfq['id'],
  options: { shouldFetch?: boolean } = { shouldFetch: true },
) {
  const drfqService = useDrfqService();
  const [{ isLoading, resp }, getRfqOrderbook] = useApi(
    drfqService.getRfqOrderbook,
  );

  useEffect(() => {
    if (options.shouldFetch === true) getRfqOrderbook({ rfqId });
  }, [getRfqOrderbook, options.shouldFetch, rfqId]);

  return {
    isLoading,
    error: resp?.ok === false ? resp.message : '',
    asks: resp?.ok === true ? resp.data.asks : [],
    bids: resp?.ok === true ? resp.data.bids : [],
  };
}

export function useDrfqOrdersFetcher(
  rfqId: Drfq['id'],
  options = { shouldFetch: true },
) {
  const drfqService = useDrfqService();
  const [{ isLoading, resp }, getRfqOrders] = useApi(drfqService.getRfqOrders);
  useEffect(() => {
    if (options.shouldFetch) getRfqOrders({ rfqId });
  }, [getRfqOrders, options.shouldFetch, rfqId]);
  return {
    isLoading,
    orders: resp?.ok === true ? resp.data.results : [],
  };
}

export function useDrfqTradesFetcher(
  rfqId: Drfq['id'],
  options = { shouldFetch: true },
) {
  const drfqService = useDrfqService();
  const [{ isLoading, resp }, getRfqTrades] = useApi(drfqService.listTrades);
  useEffect(() => {
    if (options.shouldFetch) getRfqTrades({ rfqId });
  }, [getRfqTrades, options.shouldFetch, rfqId]);
  return {
    isLoading,
    trades: resp?.ok === true ? resp.data.results : [],
  };
}

/**
 * Single home for "is this RFQ executed?" in the taker UI.
 *
 * Executed = a FILLED trade (any venue) OR, for async-settlement venues,
 * a PENDING_SETTLEMENT trade. Async venues (Paradex) settle on-chain ~60s
 * after submit; we surface the trade as done from the pending-settlement
 * moment so the taker gets a CEX-like instant fill.
 *
 * Live: the WS pushes the trade into the unified trades store, so an
 * already-rendered row updates the instant SUBMITTED arrives.
 * Reload: the store starts empty, so we fetch from REST (which returns
 * PENDING_SETTLEMENT trades) for closed-executed RFQs and for open
 * async-venue RFQs still mid-settlement.
 */
export function useDrfqExecutionState(
  drfq: Pick<Drfq, 'id' | 'venue' | 'state' | 'closed_reason'>,
) {
  const tradesService = useUnifiedTradesService();
  const tradesObservable = useMemo(
    () => tradesService.list(),
    [tradesService],
  );
  const allTrades = useObservable(tradesObservable);

  const liveTrade = useMemo(
    () =>
      allTrades.find(
        (trade): trade is DrfqTrade =>
          trade.rfqType === 'drfq' && trade.rfq_id === drfq.id,
      ),
    [allTrades, drfq.id],
  );

  const needsFetch =
    liveTrade == null &&
    (drfq.closed_reason === 'EXECUTION_LIMIT' ||
      (isAsyncVenue(drfq.venue) && drfq.state === 'OPEN'));

  const { isLoading, trades: fetchedTrades } = useDrfqTradesFetcher(drfq.id, {
    shouldFetch: needsFetch,
  });

  const executedTrade = useMemo(() => {
    const candidate = liveTrade ?? fetchedTrades.find(isExecutedTrade);
    return candidate != null && isExecutedTrade(candidate)
      ? candidate
      : undefined;
  }, [liveTrade, fetchedTrades]);

  return { isLoading, executedTrade, isExecuted: executedTrade != null };
}

export function useSplittedOrders(rfqId: Drfq['id']) {
  const orders = useDrfqOrders(rfqId);

  return useMemo(() => splitOrders(orders), [orders]);
}

export function splitOrders(orders: readonly Order[]) {
  const bids: Order[] = [];
  const asks: Order[] = [];
  let executedAsk: Order | null = null;
  let executedBid: Order | null = null;

  for (const order of orders) {
    if (isBidOrder(order)) {
      bids.push(order);
      if (isOrderExecuted(order)) {
        executedBid = order;
      }
    } else {
      asks.push(order);
      if (isOrderExecuted(order)) {
        executedAsk = order;
      }
    }
  }

  return { bids, asks, executedAsk, executedBid };
}

export function useDrfqTradeTapeFetcher(
  rfqId: Drfq['id'],
  options = { shouldFetch: true },
): {
  isLoading: boolean;
  trade: TradeTapeItem | null;
  errorMessage: string;
} {
  const drfqService = useDrfqService();
  const [{ isLoading, resp }, listTradeTape] = useApi(
    drfqService.listTradeTape,
  );

  useEffect(() => {
    if (options.shouldFetch) listTradeTape({ rfqId });
  }, [listTradeTape, rfqId, options.shouldFetch]);

  return {
    isLoading,
    trade: resp?.ok === true ? resp.data.results[0] ?? null : null,
    errorMessage: resp?.ok === false ? resp.message : '',
  };
}

export function useTakerExecutedRfqInfo(
  drfqId: Drfq['id'],
  takerSide: Side | null | undefined,
) {
  const { drfq } = useFetchedDrfq(drfqId);
  const ordersView = useSplittedOrders(drfqId);
  const [bids, asks] = useDrfqOrderbook(drfqId);

  if (takerSide == null || drfq?.role !== 'TAKER') {
    return {
      executedOrder: null,
      executedFromDesk: null,
      executedAccount: null,
    };
  }

  const makerDesk = takerSide === 'BUY' ? asks[0]?.desk : bids[0]?.desk;
  return {
    executedFromDesk: makerDesk ?? null,
    executedOrder: ordersView.executedBid ?? ordersView.executedAsk,
    executedAccount: drfq.account_name,
  };
}
